/** @jsxImportSource @emotion/react */

import React, { useState, forwardRef, useEffect } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { configState } from "../lib/State";
import { Box, Checkbox } from "@mui/material";
import CenterSelect from "./CenterSelect";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import { TextareaAutosize } from "@material-ui/core";

// 등록하기 팝업
export default function ReportMarketingEdit({ login, onClose, seq }) {
  const token = localStorage.getItem("token");
  const [tcid, setTcid] = useState("");
  const [state, setState] = useState("");
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const [memo, setMemo] = useState("");
  const [type, setType] = useState(null); // 저장, 검수
  const [sns, setSns] = useState({
    insta: "",
    face: "",
    blog: "",
  });

  useEffect(() => {
    const getData = async () => {
      const result = await axios.post(
        `${process.env.REACT_APP_API}/gympass_admin/ma_report_one`,
        {
          token: token,
          level: login.level,
          mcd: login.mcd,
          seq,
          type,
        }
      );
      if (!result?.data?.success) return;
      setRows(result.data.rows);
      setTitle(result.data.title);
      setSns(result.data.sns);
      setTcid(result.data.tcid);
      setState(result.data.state);
      setMemo(result.data.memo);
    };
    getData();
  }, []);

  useEffect(() => {
    if (!type) return;
    onSubmit();
  }, [type]);

  const onSubmit = async () => {
    setType(null);
    let result = await axios.post(
      `${process.env.REACT_APP_API}/gympass_admin/ma_report_edit`,
      {
        token: token,
        level: login.level,
        mcd: login.mcd,
        rows,
        title,
        cid: tcid,
        sns,
        seq,
        type,
        memo,
      }
    );
    if (result.data.success === 0) {
      Swal.fire("Error", result.data.msg);
      return;
    } else {
      onClose();
    }
  };

  const onDelete = async () => {
    let result = await axios.post(
      `${process.env.REACT_APP_API}/gympass_admin/ma_report_delete`,
      {
        token: token,
        level: login.level,
        mcd: login.mcd,
        seq,
      }
    );
    if (result.data.success === 0) {
      Swal.fire("Error", result.data.msg);
      return;
    } else {
      onClose();
    }
  };

  const changeData = (target, value, index) => {
    let copyArray = [...rows];
    if (index !== undefined) {
      const copy = copyArray[index];
      copy[target] = value ? "Y" : "N";
    } else {
      for (let i in copyArray) {
        const copy = copyArray[i];
        copy[target] = value ? "Y" : "N";
      }
    }
    setRows(copyArray);
  };

  return (
    <Box sx={{ padding: 2 }}>
      {/* 가맹점 */}
      <CenterSelect login={login} tcidParent={tcid} setTcidParent={setTcid} />
      {/* 제목 */}
      <Box>
        <TextField
          sx={{ width: "100%", mt: 1 }}
          label="제목"
          size="small"
          onChange={(e) => setTitle(e.target.value)}
          autoComplete="off"
          value={title}
        />
      </Box>
      {/* sns */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {[
          { title: "인스타", key: "insta" },
          { title: "페이스북", key: "face" },
          { title: "블로그", key: "blog" },
        ].map((data, index) => (
          <React.Fragment key={index}>
            <TextField
              sx={{ width: "100%", mt: 1 }}
              name={data.key}
              label={data.title}
              size="small"
              onChange={(e) =>
                setSns((state) => ({
                  ...state,
                  [e.target.name]: e.target.value,
                }))
              }
              autoComplete="off"
              value={sns[data.key] || ""}
            />
            <FileOpenIcon
              sx={{ opacity: "0.5", cursor: "pointer" }}
              onClick={() => window.open(sns[data.key] || "", "_blank")}
            />
          </React.Fragment>
        ))}
      </Box>
      <Box>
        <TextareaAutosize
          minRows={3}
          maxRows={10}
          style={{
            width: "100%",
            resize: "none",
            borderColor: "rgba(0, 0, 0, 0.23)",
            borderRadius: "4px",
            marginTop: "1rem",
            fontSize: "1rem",
            padding: "8.5px 14px",
          }}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="본사 비고란"
          disabled={login.level !== "500"}
        />
      </Box>
      {/* 리스트 */}
      <Box
        sx={{
          fontSize: "1rem",
          padding: 2,
          mt: 3,
        }}
      >
        <Box
          sx={{
            padding: "1rem 0.3rem 0",
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: 1,
            textAlign: "center",
            fontSize: "1.1rem",
            fontWeight: "bold",
            opacity: 0.8,
            borderBottom: "1px solid lightgray",
            borderTop: "1px solid lightgray",
          }}
        >
          <Box>번호</Box>
          <Box sx={{ gridColumn: "2/11" }}>문항</Box>
          <Box>이행</Box>
          <Box>
            검수
            <Checkbox
              disabled={login.level !== "500"}
              onChange={(e) => changeData("admin", e.target.checked)}
              checked={rows.filter((x) => !(x.admin === "Y")).length === 0}
            />
          </Box>
        </Box>
        {rows.map((data, index) => (
          <Box
            key={index}
            sx={{
              padding: "0.5rem 0.3rem",
              backgroundColor: index % 2 !== 0 ? "rgba(0,0,0,0.15)" : "",
              borderRadius: "7px",
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box sx={{ opacity: 0.6, textAlign: "center" }}>{index + 1}</Box>
            <Box sx={{ flex: 1, gridColumn: "2/11" }}>{data.question}</Box>
            <Checkbox
              disabled={login.level === "500"}
              onChange={(e) => changeData("center", e.target.checked, index)}
              checked={data.center === "Y"}
            />
            <Checkbox
              disabled={login.level !== "500"}
              onChange={(e) => changeData("admin", e.target.checked, index)}
              checked={data.admin === "Y"}
            />
          </Box>
        ))}
      </Box>
      {/* 버튼 */}
      <Box
        sx={{
          mt: 5,
          textAlign: "center",
          display: "flex",
          gap: 1,
          justifyContent: "center",
        }}
      >
        {login.level === "500" && (
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setType("confirm");
            }}
          >
            검수완료
          </Button>
        )}
        <Button
          variant="contained"
          onClick={() => setType("save")}
          disabled={state === "마감" && login.level !== "500"}
        >
          등록완료
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onDelete}
          disabled={state === "마감" && login.level !== "500"}
        >
          삭제
        </Button>
        <Button variant="contained" color="warning" onClick={onClose}>
          닫기
        </Button>
      </Box>
    </Box>
  );
}
